import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import type { AccountNotification } from '@/api/types';
import { Icon } from '@/components/ui/Icon';
import { formatWhen } from '@/pages/AccountsPage';

/** Every notification query; one invalidation refreshes the bell, its list and the page. */
export const NOTIFICATIONS_KEY = ['account-notifications'] as const;

/** Where a notification leads: the tenant it is about. */
export const notificationLink = (n: Pick<AccountNotification, 'tenant_id'>) => `/accounts/${n.tenant_id}`;

/**
 * The top bar's bell (2026-09-30): account changes the collectors noticed between two runs — new users and accounts,
 * apps switched on, plan and status changes. The badge is the true unread count; the dropdown shows the latest few with
 * "mark all as read" and a link to the Notifications page. Read state is shared by every admin.
 *
 * These come from diffing successive collector snapshots, so they need no Kafka and no cooperation from any other
 * service. The trade is resolution: a user added and removed between two syncs is never seen.
 */
export function AccountNotifications() {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const count = useQuery({ queryKey: [...NOTIFICATIONS_KEY, 'count'], queryFn: api.unreadNotificationCount, staleTime: 60_000, retry: false });
  const list = useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'latest'],
    queryFn: () => api.accountNotifications(false, 6),
    enabled: open,
    retry: false,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  const ack = useMutation({ mutationFn: (id: string) => api.ackAccountNotification(id), onSettled: refresh });
  const ackAll = useMutation({ mutationFn: api.ackAllAccountNotifications, onSettled: refresh });

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  // A failed count hides the badge, never shows a zero it did not read.
  const unread = count.data?.unread ?? 0;
  const items = list.data ?? [];

  return (
    <div className="account-menu" ref={box}>
      <button
        type="button"
        className="icon-button bell-button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications: ${unread} unread` : 'Notifications'}
      >
        <Icon name="bell" size={18} />
        {unread > 0 && <span className="bell-badge" aria-hidden="true">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="account-dropdown notif-dropdown" role="dialog" aria-label="Notifications">
          <div className="notif-head">
            <strong className="t-heading">Account changes</strong>
            {unread > 0 && (
              <button type="button" className="btn btn-ghost btn-sm" disabled={ackAll.isPending} onClick={() => ackAll.mutate()}>
                Mark all as read
              </button>
            )}
          </div>
          {list.isPending ? (
            <div className="search-empty t-caption muted">Loading…</div>
          ) : list.isError ? (
            <div className="search-empty t-caption note-error">Could not load the changes.</div>
          ) : items.length === 0 ? (
            <div className="search-empty t-caption muted">Nothing new since the last collector run.</div>
          ) : (
            <ul className="notif-list">
              {items.map((n) => (
                <li key={n.id} className="notif-row">
                  <Link to={notificationLink(n)} className="notif-link" onClick={() => setOpen(false)}>
                    <span className="t-body">{n.summary}</span>
                    <span className="t-caption muted">{n.tenant_name || 'an account'} · {formatWhen(n.detected_at)}</span>
                  </Link>
                  <button type="button" className="icon-button" onClick={() => ack.mutate(n.id)} disabled={ack.isPending}
                    aria-label={`Mark “${n.summary}” as read`}>
                    <Icon name="close" size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Link to="/notifications" className="menu-row" onClick={() => setOpen(false)}>See all notifications</Link>
        </div>
      )}
    </div>
  );
}
